export const DRIVER_SEARCH_MAX_OPTIONS = 50;
export const DRIVER_SEARCH_MIN_CHARS = 1;

function toSearchString(value) {
  if (typeof value === "string") {
    return value;
  }
  if (value == null) {
    return "";
  }
  return String(value);
}

export function normalizeDriverSearchValue(value) {
  let normalizedValue = toSearchString(value).trim().toLowerCase();
  if (typeof normalizedValue.normalize === "function") {
    normalizedValue = normalizedValue
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }
  return normalizedValue;
}

function getDriverData(item, driversById = {}) {
  if (!Array.isArray(item) || item.length === 0) {
    return null;
  }

  const driverId = toSearchString(item[0]);
  if (!driverId) {
    return null;
  }

  const itemData = item[1] && typeof item[1] === "object" ? item[1] : {};
  const storeData =
    driversById &&
    driversById[driverId] &&
    typeof driversById[driverId] === "object"
      ? driversById[driverId]
      : {};

  return {
    id: driverId,
    data: {
      ...itemData,
      ...storeData,
    },
  };
}

function matchesDriverEntry(entry, normalizedQuery) {
  return (
    normalizeDriverSearchValue(entry.searchValue).includes(normalizedQuery) ||
    normalizeDriverSearchValue(entry.name).includes(normalizedQuery) ||
    normalizeDriverSearchValue(entry.id).includes(normalizedQuery)
  );
}

export function createDriverSearchEntries(driversList = [], driversById = {}) {
  const nameCounts = new Map();
  const baseEntries = [];

  for (let index = 0; index < driversList.length; index++) {
    const resolvedDriver = getDriverData(driversList[index], driversById);
    if (!resolvedDriver) {
      continue;
    }

    const driverName =
      toSearchString(resolvedDriver.data.name).trim() || resolvedDriver.id;
    const normalizedName = normalizeDriverSearchValue(driverName);
    nameCounts.set(normalizedName, (nameCounts.get(normalizedName) || 0) + 1);
    baseEntries.push({
      index,
      id: resolvedDriver.id,
      name: driverName,
      chatId: toSearchString(resolvedDriver.data.chatId).trim(),
    });
  }

  return baseEntries.map((entry) => {
    const normalizedName = normalizeDriverSearchValue(entry.name);
    const hasDuplicateName =
      normalizedName !== "" && nameCounts.get(normalizedName) > 1;
    const searchValue =
      hasDuplicateName && entry.name !== entry.id
        ? `${entry.name} (${entry.id})`
        : entry.name;

    return {
      ...entry,
      searchValue,
    };
  });
}

export function buildDriverSearchMatches(
  entries = [],
  query = "",
  showDefaults = false,
  maxOptions = DRIVER_SEARCH_MAX_OPTIONS
) {
  const normalizedQuery = normalizeDriverSearchValue(query);
  if (!showDefaults && normalizedQuery.length < DRIVER_SEARCH_MIN_CHARS) {
    return [];
  }

  const options = [];
  for (
    let index = 0;
    index < entries.length && options.length < maxOptions;
    index++
  ) {
    const entry = entries[index];
    if (!entry) {
      continue;
    }
    if (showDefaults || matchesDriverEntry(entry, normalizedQuery)) {
      options.push(entry);
    }
  }
  return options;
}

export function buildDriverSearchOptions(
  entries = [],
  query = "",
  showDefaults = false,
  maxOptions = DRIVER_SEARCH_MAX_OPTIONS
) {
  return buildDriverSearchMatches(
    entries,
    query,
    showDefaults,
    maxOptions
  ).map((entry) => entry.searchValue);
}

export function resolveDriverSearchSelection(entries = [], query = "") {
  const normalizedQuery = normalizeDriverSearchValue(query);
  if (!normalizedQuery) {
    return null;
  }

  const exactIdMatches = [];
  const exactSearchValueMatches = [];
  const exactNameMatches = [];
  const partialMatches = [];
  for (let index = 0; index < entries.length; index++) {
    const entry = entries[index];
    if (!entry) {
      continue;
    }

    if (normalizeDriverSearchValue(entry.id) === normalizedQuery) {
      exactIdMatches.push(entry);
    }
    if (normalizeDriverSearchValue(entry.searchValue) === normalizedQuery) {
      exactSearchValueMatches.push(entry);
    }
    if (normalizeDriverSearchValue(entry.name) === normalizedQuery) {
      exactNameMatches.push(entry);
    }

    if (matchesDriverEntry(entry, normalizedQuery)) {
      partialMatches.push(entry);
    }
  }

  if (exactIdMatches.length === 1) {
    return exactIdMatches[0];
  }
  if (exactSearchValueMatches.length === 1) {
    return exactSearchValueMatches[0];
  }
  if (exactNameMatches.length === 1) {
    return exactNameMatches[0];
  }
  if (exactNameMatches.length > 1) {
    return null;
  }
  return partialMatches.length === 1 ? partialMatches[0] : null;
}
